import React, { useState, useRef } from 'react';
import { 
  Activity, 
  Mic, 
  Volume2, 
  AlertTriangle
} from 'lucide-react';
import { GoogleGenAI, LiveServerMessage, Modality } from '@google/genai';

interface LiveConsultProps {
  isAuthenticated: boolean;
}

export const LiveConsult: React.FC<LiveConsultProps> = ({ isAuthenticated }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [transcription, setTranscription] = useState<string[]>([]);
  
  const aiRef = useRef<GoogleGenAI | null>(null);
  const sessionRef = useRef<any>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const playbackContextRef = useRef<AudioContext | null>(null);
  const nextPlayTimeRef = useRef<number>(0);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const startConsultation = async () => {
    if (!isAuthenticated) {
      alert("Session expired. Please log in again.");
      return;
    }

    // Use the injected API key if available, otherwise use a placeholder
    // The server-side proxy will replace the placeholder with the real key from Secrets
    const apiKey = process.env.GEMINI_API_KEY || "MY_GEMINI_API_KEY";
    
    console.log("startConsultation triggered!");
    setIsConnecting(true);
    setTranscription([]); 
    
    try {
      console.log("Requesting microphone access...");
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      console.log("Initializing AudioContexts...");
      const AudioContextClass = (window.AudioContext || (window as any).webkitAudioContext);
      audioContextRef.current = new AudioContextClass({ sampleRate: 16000 });
      playbackContextRef.current = new AudioContextClass({ sampleRate: 24000 });
      nextPlayTimeRef.current = 0;

      await audioContextRef.current.resume();
      await playbackContextRef.current.resume();
      
      sourceRef.current = audioContextRef.current.createMediaStreamSource(stream);
      processorRef.current = audioContextRef.current.createScriptProcessor(4096, 1, 1);
      
      console.log("Connecting to Gemini Live API via SDK...");
      const ai = new GoogleGenAI({ apiKey });
      aiRef.current = ai;

      const session = await ai.live.connect({
        model: "gemini-2.5-flash-native-audio-preview-09-2025",
        callbacks: {
          onopen: () => {
            console.log("CONNECTED: Live session opened successfully.");
            setIsRecording(true);
            setIsConnecting(false);
            
            if (processorRef.current && sourceRef.current && audioContextRef.current) {
              processorRef.current.onaudioprocess = (e) => {
                const inputData = e.inputBuffer.getChannelData(0);
                const pcmData = new Int16Array(inputData.length);
                for (let i = 0; i < inputData.length; i++) {
                  pcmData[i] = Math.max(-1, Math.min(1, inputData[i])) * 0x7FFF;
                }
                const base64Data = btoa(String.fromCharCode(...new Uint8Array(pcmData.buffer)));
                
                session.sendRealtimeInput({
                  media: { data: base64Data, mimeType: 'audio/pcm;rate=16000' }
                });
              };
              sourceRef.current.connect(processorRef.current);
              processorRef.current.connect(audioContextRef.current.destination);
            }
          },
          onmessage: async (message: LiveServerMessage) => {
            console.log("SDK Message Received:", message);

            if (message.serverContent?.interrupted) {
              console.log("Agent interrupted");
              if (playbackContextRef.current) {
                nextPlayTimeRef.current = playbackContextRef.current.currentTime;
              }
            }

            const base64Audio = message.serverContent?.modelTurn?.parts[0]?.inlineData?.data;
            if (base64Audio && playbackContextRef.current) {
              const binaryString = atob(base64Audio);
              const bytes = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
              }
              
              const audioCtx = playbackContextRef.current;
              const pcm16 = new Int16Array(bytes.buffer);
              const audioBuffer = audioCtx.createBuffer(1, pcm16.length, 24000);
              const channelData = audioBuffer.getChannelData(0);
              for (let i = 0; i < pcm16.length; i++) {
                channelData[i] = pcm16[i] / 32768.0;
              }
              
              const source = audioCtx.createBufferSource();
              source.buffer = audioBuffer;
              source.connect(audioCtx.destination);
              
              const currentTime = audioCtx.currentTime;
              let playTime = nextPlayTimeRef.current;
              
              if (playTime < currentTime) {
                playTime = currentTime + 0.05;
              }
              
              source.start(playTime);
              nextPlayTimeRef.current = playTime + audioBuffer.duration;
            }
            
            const text = message.serverContent?.modelTurn?.parts[0]?.text;
            if (text) {
               setTranscription(prev => [...prev, text]);
            }
          },
          onerror: (error: any) => {
            console.error("SDK Live Error:", error);
            alert(`Gemini Error: ${error.message || "The connection failed. Please ensure your API key is correct and has access to the Multimodal Live API."}`);
            stopConsultation();
          },
          onclose: (event) => {
            console.log("Live session closed.", event.code, event.reason);
            setIsRecording(false);
            setIsConnecting(false);
          }
        },
        config: {
          responseModalities: [Modality.AUDIO],
          speechConfig: {
            voiceConfig: { prebuiltVoiceConfig: { voiceName: "Zephyr" } }
          },
          systemInstruction: "Role: You are a Zero-Assumption Clinical Voice Interface.\n\nCore Directive: You are strictly forbidden from assuming the identity, gender, or medical history of the speaker until they have been verified or have provided their own context. You must operate as a 'Blank Slate' that adapts instantly to the current speaker.\n\nLogic Flow for Every Interaction:\n1. Signal Detection: Listen to the voice input. Identify if the speaker is a Clinician, a Patient, or a Caregiver based on the content and intent of their speech.\n2. Identity Verification: If the speaker asks for data (e.g., 'Show me the cardiology trends'), you must respond: 'I\\'m ready to help. To ensure I pull the correct file, could you please confirm who I am speaking with?'\n3. Demand Execution: Once identity is established, execute the demand with 100% precision.\n\nHandling 'The Mr. Smith Problem': Treat every 'Start of Stream' as a new environment. Do not use pre-filled names in your greeting. Your first greeting MUST be exactly: 'Clinical Ecosystem active. How can I assist you today?'\n\nAdaptive Persona: If a doctor speaks, your tone is 'Peer-to-Peer/Efficient.' If a patient speaks, your tone is 'Empathetic/Clear.'\n\nConstraint: Never hallucinate a patient name. If the user says 'I want my results,' and you don't know who they are, ask for their ID or Name. Do not guess."
        }
      });
      sessionRef.current = session;

    } catch (err: any) {
      console.error("Failed to start consultation:", err);
      alert(`Initialization Error: ${err.message || "Unknown error occurred"}`);
      setIsConnecting(false);
      
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    }
  };

  const stopConsultation = () => {
    if (processorRef.current && sourceRef.current) {
      processorRef.current.disconnect();
      sourceRef.current.disconnect();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    if (playbackContextRef.current) {
      playbackContextRef.current.close();
      playbackContextRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (sessionRef.current) {
      sessionRef.current.close();
      sessionRef.current = null;
    }
    setIsRecording(false);
    setIsConnecting(false);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <header className="flex flex-col md:flex-row md:justify-between md:items-end gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-400 mb-2 font-medium">
            <span>Cardio-Onc AI</span>
            <span>/</span>
            <span className="text-raspberry-500 uppercase">Live Consult</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">Live AI Consultation</h2>
          <p className="text-slate-500 text-sm md:text-base">Real-time cardio-oncology support.</p>
        </div>
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-2 text-xs md:text-sm text-amber-600 font-bold border border-amber-200 bg-amber-50 px-3 py-1.5 md:px-4 md:py-2 rounded-lg">
            <AlertTriangle size={16} /> GOVERNED SESSION
          </span>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-2xl p-8 md:p-12 flex flex-col items-center justify-center min-h-[300px] md:min-h-[400px] shadow-sm">
            <div className={`w-24 h-24 md:w-32 md:h-32 rounded-full flex items-center justify-center mb-6 md:mb-8 transition-all duration-300 ${isRecording ? 'bg-raspberry-100 scale-110' : 'bg-slate-100'}`}>
              <Mic className={`w-9 h-9 md:w-12 md:h-12 ${isRecording ? 'text-raspberry-600' : 'text-slate-400'}`} />
            </div>
            
            {!isRecording ? (
              <button 
                onClick={() => {
                  console.log("Button Clicked! isConnecting:", isConnecting);
                  startConsultation();
                }}
                className={`w-full sm:w-auto bg-raspberry-600 hover:bg-raspberry-700 text-white px-6 py-3 md:px-8 md:py-4 rounded-xl font-bold text-base md:text-lg transition-all shadow-sm flex items-center justify-center gap-3 ${isConnecting ? 'opacity-70 cursor-wait' : 'active:scale-95'}`}
              >
                {isConnecting ? (
                  <><Activity size={24} className="animate-spin" /> Connecting...</>
                ) : (
                  <><Mic size={24} /> Start Consultation</>
                )}
              </button>
            ) : (
              <button 
                onClick={stopConsultation}
                className="w-full sm:w-auto bg-red-500 hover:bg-red-600 text-white px-6 py-3 md:px-8 md:py-4 rounded-xl font-bold text-base md:text-lg transition-colors shadow-sm flex items-center justify-center gap-3"
              >
                <Mic size={24} /> Stop Consultation
              </button>
            )}

            {isConnecting && (
              <button 
                onClick={() => {
                  console.log("Force Resetting Connection State");
                  setIsConnecting(false);
                }}
                className="mt-4 text-xs text-slate-400 hover:text-raspberry-500 underline transition-colors"
              >
                Reset Connection State
              </button>
            )}
          </div>

          <div className="bg-raspberry-50 border border-raspberry-100 rounded-2xl p-6 flex items-start gap-4">
            <div className="text-raspberry-600 mt-1">
              <Volume2 size={24} />
            </div>
            <div>
              <h4 className="font-bold text-raspberry-900 mb-1">Voice Synthesis</h4>
              <p className="text-sm text-raspberry-700">Powered by Gemini 2.5 Flash for sub-second latency.</p>
            </div>
          </div>
        </div>

        <div className="bg-slate-900 rounded-2xl p-6 flex flex-col h-full min-h-[500px] shadow-lg">
          <h3 className="font-bold text-white mb-6 flex items-center gap-2">
            Live Transcription
          </h3>
          <div className="flex-1 overflow-y-auto space-y-4">
            {transcription.length === 0 ? (
              <p className="text-slate-500 text-xs md:text-sm italic">Transcription will appear here...</p>
            ) : (
              transcription.map((text, i) => (
                <div key={i} className="text-slate-300 text-xs md:text-sm leading-relaxed bg-slate-800 p-3 md:p-4 rounded-lg">
                  {text}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
